import { MoneySchema } from "@redotech/redo-order-core-common/money-schema";
import * as z from "zod";
import { hsaEligibility } from "./hsa-eligibility";

// Order context passed by the merchant to `createHsaSession` and surfaced to
// the customer-portal questionnaire side panel. Two consumers:
//
//   1. v1 — render the order summary alongside the questionnaire so the
//      shopper doesn't feel jarringly removed from the Shopify checkout.
//   2. Phase 7+ — drive LMN generation (which products the LMN authorizes)
//      and split-payment routing (HSA-eligible vs non-eligible totals).
//
// `eligibility` is the resolved tri-state, as produced by the metafield-based
// resolver on the merchant side before session creation. The customer portal
// trusts the value as-is.

// Nullable: a Gale handoff supplies at most a variant id and no product id.
// The Shopify-initiated CHECKOUT path always populates both.
const hsaLineItem_ = z.object({
  externalProductId: z.string().min(1).nullable(),
  externalVariantId: z.string().min(1).nullable(),
  imageUrl: z.string().url().nullable(),
  title: z.string().min(1),
  variantTitle: z.string().nullable(),
  quantity: z.number().int().positive(),
  unitPrice: MoneySchema,
  eligibility: hsaEligibility,
});
export const hsaLineItem: z.ZodType<HsaLineItem> = hsaLineItem_;
export interface HsaLineItem extends z.infer<typeof hsaLineItem_> {}

// Shipping and estimated taxes are routinely "pending" at session creation —
// the shopper hasn't entered an address yet. Modeled explicitly so the UI
// can render "Enter shipping address" instead of stuffing a sentinel into a
// Money field.
export enum HsaPriceLineStatus {
  KNOWN = "known",
  PENDING = "pending",
}

const hsaPriceLineKnown_ = z.object({
  status: z.literal(HsaPriceLineStatus.KNOWN),
  value: MoneySchema,
});
const hsaPriceLinePending_ = z.object({
  status: z.literal(HsaPriceLineStatus.PENDING),
});
const hsaPriceLine_ = z.discriminatedUnion("status", [
  hsaPriceLineKnown_,
  hsaPriceLinePending_,
]);
export const hsaPriceLine: z.ZodType<HsaPriceLine> = hsaPriceLine_;
export type HsaPriceLine = z.infer<typeof hsaPriceLine_>;

// Discount is display-only in v1. The HSA portal is a health questionnaire,
// not a checkout re-do — interactive code entry would mean Shopify roundtrips
// for validation. Whatever the merchant has already applied, we render.
const hsaAppliedDiscount_ = z.object({
  code: z.string().min(1),
  amount: MoneySchema,
});
export const hsaAppliedDiscount: z.ZodType<HsaAppliedDiscount> =
  hsaAppliedDiscount_;
export interface HsaAppliedDiscount extends z.infer<
  typeof hsaAppliedDiscount_
> {}

const hsaOrderContext_ = z.object({
  currency: z.string().min(1),
  // May be empty: a partner handoff whose every line was folded into shipping
  // (a coverage-only Gale cart) still has summary rows to show.
  lineItems: z.array(hsaLineItem_),
  subtotal: MoneySchema,
  shipping: hsaPriceLine_,
  estimatedTaxes: hsaPriceLine_,
  total: MoneySchema,
  appliedDiscount: hsaAppliedDiscount_.nullable(),
  // Label for the shipping row when the producer folded another charge into it
  // (the Gale handoff folds coverage under Gale's "Shipping & fulfillment").
  // Absent means the renderer's own "Shipping" copy. Optional, not nullable:
  // rows persisted before the field existed carry no key at all.
  shippingLabel: z.string().min(1).optional(),
});
export const hsaOrderContext: z.ZodType<HsaOrderContext> = hsaOrderContext_;
export interface HsaOrderContext extends z.infer<typeof hsaOrderContext_> {}

// The Shopify checkout extension's variant: a Shopify checkout always has
// lines, so an empty list there is a caller bug rather than a folded cart.
export const hsaCheckoutOrderContext: z.ZodType<HsaOrderContext> =
  hsaOrderContext_.extend({
    lineItems: hsaOrderContext_.shape.lineItems.min(1),
  });
